import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../../contexts/AuthContext';
import { fetchReflectionDetail, unmergeReflection } from '../../services/dashboardService';
import { 
  ArrowLeft, 
  MessageSquare, 
  Bot,
  User,
  Calendar,
  Tag,
  Loader2,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Lightbulb,
  GitMerge,
  Undo2
} from 'lucide-react';

// Detect if mobile viewport
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

const SITUATION_COLORS = {
  feedback: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  delegation: 'bg-green-500/10 text-green-400 border-green-500/20',
  difficult_conversation: 'bg-red-500/10 text-red-400 border-red-500/20',
  decision_making: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  one_to_one: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  team_conflict: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  general: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
};

const ReflectionDetailPage = () => {
  const { conversationId } = useParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [isMerged, setIsMerged] = useState(false);
  const [mergedTitle, setMergedTitle] = useState(null);
  const [mergedFromCount, setMergedFromCount] = useState(0);
  const [unmerging, setUnmerging] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    const loadReflection = async () => {
      try {
        const data = await fetchReflectionDetail(conversationId);
        setConversation(data.conversation);
        setMessages(data.messages || []);
        setMetadata(data.metadata);
        setIsMerged(!!data.is_merged);
        setMergedTitle(data.merged_title || null);
        setMergedFromCount((data.merged_from || []).length);
      } catch (err) {
        if (err.message === 'Unauthorized') {
          navigate('/login', { replace: true });
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && conversationId) {
      loadReflection();
    }
  }, [isAuthenticated, conversationId, navigate]);

  const handleUnmerge = async () => {
    if (!conversationId) return;
    if (!window.confirm('Restore the original reflections and remove this merged thread?')) return;
    try {
      setUnmerging(true);
      await unmergeReflection(conversationId);
      navigate('/dashboard/reflections', { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to unmerge');
    } finally {
      setUnmerging(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <header className="border-b border-zinc-800">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link to="/dashboard/reflections" className="flex items-center gap-2 text-zinc-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
              Back to Reflections
            </Link>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-16 text-center">
          <MessageSquare className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Reflection not found</h2>
          <p className="text-zinc-400">{error || 'This reflection could not be loaded.'}</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800 sticky top-0 bg-zinc-950/95 backdrop-blur z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard/reflections" className="flex items-center gap-2 text-zinc-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
              Back to Reflections
            </Link>
            {isMerged ? (
              <button
                onClick={handleUnmerge}
                disabled={unmerging}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                data-testid="reflection-unmerge-btn"
              >
                {unmerging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
                Unmerge
              </button>
            ) : (
              <Link
                to={`/dashboard/chat?continue=${conversationId}`}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <MessageSquare className="w-4 h-4" />
                Continue Chat
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Merged banner */}
        {isMerged ? (
          <div
            className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 mb-6 flex items-start gap-3"
            data-testid="reflection-merged-banner"
          >
            <GitMerge className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-purple-100 font-semibold">
                {mergedTitle || 'Consolidated thread'}
              </p>
              <p className="text-purple-300/80 text-sm mt-0.5">
                Merged from {mergedFromCount} reflection{mergedFromCount === 1 ? '' : 's'}. Click "Unmerge" to restore the originals.
              </p>
            </div>
          </div>
        ) : null}

        {/* Metadata card */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="flex items-center gap-2 text-zinc-400">
              <Calendar className="w-4 h-4" />
              {new Date(conversation.created_at).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
            
            {metadata?.situation_category && (
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${SITUATION_COLORS[metadata.situation_category] || SITUATION_COLORS.general}`}>
                <Tag className="w-3 h-3 inline mr-1" />
                {metadata.situation_category.replace('_', ' ')}
              </span>
            )}
            
            {metadata?.emotional_tone && (
              <span className="text-zinc-500">
                Tone: {metadata.emotional_tone}
              </span>
            )}
            
            {metadata?.people_involved?.length > 0 && (
              <span className="text-zinc-500">
                People: {metadata.people_involved.join(', ')}
              </span>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="space-y-4">
          {messages.map((message, index) => (
            <MessageBubble key={index} message={message} isMobile={isMobile} />
          ))}
        </div>
      </main>
    </div>
  );
};

const MessageBubble = ({ message, isMobile }) => {
  const isUser = message.role === 'user';
  const [showReasoning, setShowReasoning] = useState(!isMobile);
  const hasStructuredContent = message.actions || message.reasoning;
  
  // For user messages or non-structured responses, render simple bubble
  if (isUser || !hasStructuredContent) {
    return (
      <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUser ? 'bg-blue-500/10' : 'bg-purple-500/10'
        }`}>
          {isUser ? (
            <User className="w-4 h-4 text-blue-400" />
          ) : (
            <Bot className="w-4 h-4 text-purple-400" />
          )}
        </div>
        <div className={`flex-1 max-w-[80%] ${isUser ? 'text-right' : ''}`}>
          <div className={`inline-block p-4 rounded-2xl ${
            isUser 
              ? 'bg-blue-600 text-white rounded-tr-sm' 
              : 'bg-zinc-800 text-zinc-100 rounded-tl-sm'
          }`}>
            <div className="prose prose-invert prose-sm max-w-none prose-p:my-1 prose-ul:my-2 prose-li:my-0">
              <ReactMarkdown>{message.content}</ReactMarkdown>
            </div>
          </div>
          <p className={`text-xs text-zinc-600 mt-1 ${isUser ? 'text-right' : ''}`}>
            {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>
    );
  }
  
  // Structured AI response - better layout with separated action cards
  return (
    <div className="flex gap-3">
      <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center flex-shrink-0">
        <Bot className="w-4 h-4 text-purple-400" />
      </div>
      <div className="flex-1 max-w-[90%]">
        {/* Actions section - as individual cards on mobile */}
        {message.actions && message.actions.length > 0 && (
          <div className="mb-4" data-testid="reflection-actions">
            <p className="text-red-400 font-semibold text-sm mb-3">Your next steps:</p>
            <div className="space-y-2">
              {message.actions.map((action, i) => (
                <div 
                  key={i} 
                  className="bg-red-500/10 border border-red-500/20 rounded-lg p-3"
                >
                  <div className="flex items-start gap-2 text-zinc-100">
                    <span className="text-red-400 font-bold text-lg leading-none">•</span>
                    <span className="prose prose-invert prose-sm max-w-none prose-p:my-0 prose-p:inline flex-1">
                      <ReactMarkdown>{action.text}</ReactMarkdown>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Reasoning section - collapsible on mobile */}
        {message.reasoning && (
          <div className="mb-3">
            {isMobile && !showReasoning ? (
              <button 
                onClick={() => setShowReasoning(true)}
                className="text-purple-400 text-sm flex items-center gap-1 hover:text-purple-300 transition-colors py-2"
                data-testid="show-reasoning-button"
              >
                <ChevronDown className="w-4 h-4" />
                Show reasoning
              </button>
            ) : (
              <div className="bg-zinc-800 rounded-xl p-4" data-testid="reflection-reasoning">
                {isMobile && (
                  <button 
                    onClick={() => setShowReasoning(false)}
                    className="text-purple-400 text-sm flex items-center gap-1 hover:text-purple-300 transition-colors mb-3"
                  >
                    <ChevronUp className="w-4 h-4" />
                    Hide reasoning
                  </button>
                )}
                <div className="prose prose-invert prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-0 text-zinc-300">
                  <ReactMarkdown>{message.reasoning}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Follow-up prompt */}
        {message.follow_up && (
          <div className="prose prose-invert prose-sm max-w-none prose-p:my-0 text-zinc-500 italic mb-3" data-testid="reflection-followup">
            <ReactMarkdown>{message.follow_up}</ReactMarkdown>
          </div>
        )}
        
        {/* Book reference card */}
        {message.book_reference && (
          <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-4 mb-3" data-testid="reflection-book-reference">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-4 h-4 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-amber-400 font-semibold text-sm mb-1">
                  {message.book_reference.day_number 
                    ? `Day ${message.book_reference.day_number}: ${message.book_reference.day_title}`
                    : message.book_reference.book_title
                  }
                </p>
                {message.book_reference.relevance_note && (
                  <p className="text-zinc-300 text-sm mb-2">{message.book_reference.relevance_note}</p>
                )}
                {message.book_reference.key_takeaway && (
                  <div className="flex items-start gap-2 mt-2 p-2 bg-zinc-800/50 rounded-lg">
                    <Lightbulb className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <p className="text-zinc-300 text-sm italic">{message.book_reference.key_takeaway}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Timestamp */}
        <p className="text-xs text-zinc-600">
          {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </div>
  );
};

export default ReflectionDetailPage;
